import { elementTypes } from './ElementTypes'
import { EditorState, SelectionState } from 'draft-js'
import { downloadFullFont, setCustomFonts } from '@/components/commons/FontManager/fontDownload'
import { stateToHTML } from 'draft-js-export-html'

/** Elements */
import Image from '@/assets/images/elements/image.svg'
import Video from '@/assets/images/elements/video.svg'
import Iframe from '@/assets/images/elements/iframe.svg'

//Inline style
export const INLINE_STYLES = [
  {
    symbol: 'B',
    type: 'BOLD',
    value: { fontWeight: 'bold' },
    key: 'fontWeight'
  },
  {
    symbol: 'I',
    type: 'ITALIC',
    value: { fontStyle: 'italic' },
    key: 'fontStyle'
  },
  {
    symbol: 'U',
    type: 'UNDERLINE',
    value: { textDecoration: 'underline' },
    key: 'textDecoration'
  }
]

// Align items
export const ALIGN_STYLES = [
  { value: { textAlign: 'left' }, icon: 'align_left' },
  { value: { textAlign: 'center' }, icon: 'align_center' },
  { value: { textAlign: 'right' }, icon: 'align_right' }
]

// Border type
export const BORDER = ['hidden', 'groove', 'solid', 'double']

// Border sizes
export const borderSizes = Array.from({ length: 30 }, (v, k) => k + 1)

// Font sizes
export const fontSizes = Array.from({ length: 80 }, (v, k) => k + 1)

// Font height
export const fontHeight = Array.from({ length: 160 }, (v, k) => k + 1)

/**
 * Return all text selected
 * @param {EditorState} editorState
 */
export const getAllTextSelect = editorState => {
  const currentContent = editorState.getCurrentContent()
  const firstBlock = currentContent.getBlockMap().first()
  const lastBlock = currentContent.getBlockMap().last()
  const firstBlockKey = firstBlock.getKey()
  const lastBlockKey = lastBlock.getKey()
  const lengthOfLastBlock = lastBlock.getLength()
  return new SelectionState({
    anchorKey: firstBlockKey,
    anchorOffset: 0,
    focusKey: lastBlockKey,
    focusOffset: lengthOfLastBlock
  })
}

/**
 * Check fonts on text object to donwload
 * @param {Object} fontFamilyMap
 */
export const donwloadUsedFonts = fontFamilyMap => {
  Object.entries(fontFamilyMap).map(([key, value]) => {
    let name = key.split('fontFamily-')[1]
    name = name.replace(/-/g, ' ')
    if (value.fontPath) {
      setCustomFonts(name, value.fontPath)
    }
    if (value.fontFamily && !value.fontPath) {
      downloadFullFont(name, Math.floor(Math.random() * 3000), ['regular'], false)
    }
    return key
  })
}

/**
 * Save a version of the text in html format
 * @param {EditorState} editorState
 */
export const prepareToSaveHTML = editorState => {
  const options = {
    defaultBlockTag: 'div',
    inlineStyleFn: styles => {
      const _styles = {}

      styles
        .filter(value => value.startsWith('fontSize-'))
        .map(item => {
          const size = item.replace('fontSize-', '')
          const hasPx = size.indexOf('px') !== -1
          _styles.fontSize = hasPx ? size : `${size}px`
          return item
        })

      styles
        .filter(value => value.startsWith('color-'))
        .map(color => {
          _styles.color = color.replace('color-', '')
          return color
        })

      styles
        .filter(value => value.startsWith('fontFamily-'))
        .map(family => {
          _styles.fontFamily = family.split('fontFamily-')[1].replace(/-/g, ' ')
          return family
        })

      if (!_styles.fontSize) {
        _styles.fontSize = '14px'
      }

      return {
        element: 'span',
        style: _styles
      }
    },
    entityStyleFn: entity => {
      const entityType = entity.get('type').toLowerCase()
      if (entityType === 'link') {
        const { url, target, page } = entity.getData()

        if (url) {
          return {
            element: 'a',
            attributes: {
              href: url,
              target
            },
            style: {
              color: 'blue',
              display: 'inline'
            }
          }
        } else if (page) {
          return {
            element: 'a',
            attributes: {
              href: `#page-${page}`
            },
            style: {
              cursor: 'pointer',
              color: 'blue',
              display: 'inline'
            }
          }
        }
        else {
          return {
            element: 'span'
          }
        }
      }
    }
  }
  const html = stateToHTML(editorState.getCurrentContent(), options)
  return html
}

/**
 * ====================================================================
 * EXPORT DEFAULT ELEMENTS STYLES
 * ====================================================================
 */

export const defaultElementStyles = {
  [elementTypes.TEXT]: {
    text: 'Digite um texto',
    styles: {
      color: '#666',
      width: '200px',
      height: '80px',
      textShadow: '0px 0px 0px #000',
      fontSize: '14px',
      opacity: 1,
      lineHeight: '14px'
    },
    rawElement: '',
    attributes: {
      transform: 'rotate(0deg)',
      left: '100px',
      top: '100px',
      gradient: []
    },
    editorState: EditorState.createEmpty(),
    rawState: null,
    colorsMap: {},
    fontSizeMap: {},
    fontFamilyMap: {},
    html: null
  },
  [elementTypes.BUTTON]: {
    text: 'Meu botão',
    styles: {
      color: '#666',
      backgroundColor: '#fff',
      fontSize: '14px',
      fontWeight: 'normal',
      fontFamily: 'Verdana',
      width: '100px',
      height: '80px',
      opacity: 1
    },
    rawElement: '',
    attributes: {
      transform: 'rotate(0deg)',
      left: '100px',
      top: '100px',
      gradient: []
    }
  },
  [elementTypes.IMAGE]: {
    text: '',
    styles: {
      width: '200px',
      height: '200px',
      opacity: 1
    },
    rawElement: '',
    attributes: {
      transform: 'rotate(0deg)',
      left: '100px',
      top: '100px',
      src: Image,
      gradient: []
    }
  },
  [elementTypes.SQUARE]: {
    styles: {
      backgroundColor: 'rgba(0,0,0,1)',
      width: '200px',
      height: '200px',
      opacity: 1
    },
    rawElement: '',
    attributes: {
      transform: 'rotate(0deg)',
      left: '100px',
      gradient: []
    }
  },
  [elementTypes.RHOMBUS]: {
    viewBox: '0 0 80 80',
    attrShape: {
      path: {
        d: 'M40.04-.01l39.957 39.97L39.96 80.008 0 40.04z'
      }
    },
    styles: {
      fill: '#385a7c',
      width: '200px',
      height: '200px',
      opacity: 1
    },
    rawElement: '',
    attributes: {
      left: '100px',
      top: '100px',
      gradient: []
    }
  },
  [elementTypes.CIRCLE]: {
    styles: {
      backgroundColor: '#6666',
      width: '200px',
      height: '200px',
      MozBorderRadius: '50%',
      WebkitBorderRadius: '50%',
      borderRadius: '50%',
      opacity: 1
    },
    rawElement: '',
    attributes: {
      transform: 'rotate(0deg)',
      left: '100px',
      top: '100px',
      gradient: []
    }
  },
  [elementTypes.LINE]: {
    styles: {
      backgroundColor: '#6666',
      width: '200px',
      height: '4px',
      opacity: 1
    },
    rawElement: '',
    attributes: {
      transform: 'rotate(0deg)',
      left: '100px',
      top: '100px',
      gradient: []
    }
  },
  [elementTypes.TRIANGLE]: {
    viewBox: '0 0 80 80',
    attrShape: {
      path: {
        d: 'M39.757 0L0 80h80z'
      }
    },
    styles: {
      fill: '#385a7c',
      width: '100px',
      height: '100px',
      opacity: 1
    },
    rawElement: '',
    attributes: {
      left: '100px',
      top: '100px',
      gradient: []
    }
  },
  [elementTypes.IFRAME]: {
    styles: {
      width: '350px',
      height: '250px',
      border: 0,
      opacity: 1,
    },
    content: {
      src: Iframe,
      embedURL: '',
      embedDocument: ''
    },
    attributes: {
      transform: 'rotate(0deg)',
      left: '100px',
      top: '100px',
      gradient: []
    }
  },
  [elementTypes.VIDEO]: {
    styles: {
      width: '350px',
      height: '250px',
      border: 0,
      opacity: 1,
    },
    src: {
      platform: 'vimeo',
      image: Video,
      video: 'https://player.vimeo.com/video/384459672?'
    },
    options: {
      time: {
        enabled: false,
        start: 0,
        end: 0,
      },
      noControls: false,
      autoplay: false,
      loop: false,
    },
    attributes: {
      transform: 'rotate(0deg)',
      left: '100px',
      top: '100px',
      gradient: []
    }
  },
  [elementTypes.ARROW]: {
    viewBox: '0 0 100 15',
    attrShape: {
      path: {
        d: "M0.792893 6.79289C0.402367 7.18341 0.402367 7.81657 0.792892 8.2071L7.15685 14.5711C7.54738 14.9616 8.18054 14.9616 8.57107 14.5711C8.96159 14.1805 8.96159 13.5474 8.57107 13.1568L2.91422 7.49999L8.57107 1.84314C8.96159 1.45261 8.96159 0.819449 8.57107 0.428925C8.18054 0.0384003 7.54738 0.0384002 7.15685 0.428925L0.792893 6.79289ZM94.5 6.5L1.5 6.49999L1.5 8.49999L94.5 8.5L94.5 6.5Z"
      }
    },
    styles: {
      fill: '#385a7c',
      width: '100px',
      height: '15px',
      opacity: 1
    },
    rawElement: '',
    attributes: {
      transform: 'rotate(0deg)',
      left: '100px',
      top: '100px',
      gradient: []
    }
  },
  [elementTypes.STAR]: {
    viewBox: '0 0 89 85',
    attrShape: {
      path: {
        d: "M44.5 0L54.9399 32.1307H88.7241L61.3921 51.9886L71.832 84.1193L44.5 64.2614L17.168 84.1193L27.6079 51.9886L0.275871 32.1307H34.0601L44.5 0Z"
      }
    },
    styles: {
      fill: '#385a7c',
      width: '100px',
      height: '100px',
      opacity: 1
    },
    rawElement: '',
    attributes: {
      left: '100px',
      top: '100px',
      gradient: []
    }
  },
}



// WEBPACK FOOTER //
// ./src/components/commons/editor/configs/ElementsConfig.js